import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Loader2, Pause, Clock, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Wraps admin/worker layouts. Shows:
 *  - Small banner during last 3 days of trial ("Cancel here if you don't want to continue")
 *  - Full pause overlay when status == "paused" (payment failed)
 *  - Cancel confirmation modal (opened via ?cancel=1 deep-link or settings)
 */
const SubscriptionGuard = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user || user.role === 'superadmin') {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/subscription/status`, { withCredentials: true });
      setState(res.data);
    } catch (err) {
      console.error('Subscription status fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Open cancel modal if URL has ?cancel=1 (from reminder email) — but skip if user is in /settings (the tab handles it there)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('cancel') === '1' && state && !location.pathname.includes('/settings')) {
      setShowCancelModal(true);
    }
  }, [location.search, location.pathname, state]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API_URL}/subscription/cancel`, {}, { withCredentials: true });
      if (res.data.is_in_trial) {
        toast.success('Prenumerationen är uppsagd — du loggas ut nu.');
        setTimeout(() => logout(), 2000);
      } else {
        toast.success('Uppsägning registrerad — du har åtkomst tills periodens slut.');
        setShowCancelModal(false);
        await fetchStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte säga upp prenumerationen');
    } finally {
      setCancelling(false);
    }
  };

  // Bypass for superadmin / customer / unauthenticated
  if (!user || user.role === 'superadmin' || user.role === 'customer') {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-[#E8613A] animate-spin" />
      </div>
    );
  }

  const isPaused = state?.is_paused;
  const isCancelled = state?.is_cancelled;
  const daysLeft = state?.days_remaining;
  const showTrialBanner = state?.status === 'trial' && daysLeft !== null && daysLeft <= 3 && !isPaused && !bannerDismissed;

  return (
    <>
      {showTrialBanner && (
        <div
          className="w-full bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900 px-4 py-2.5 flex flex-wrap items-center justify-center gap-3 text-sm"
          data-testid="trial-banner"
        >
          <Clock size={14} className="text-amber-700 dark:text-amber-400 flex-shrink-0" />
          <span className="text-amber-900 dark:text-amber-200">
            {daysLeft === 0
              ? 'Sista dagen av provperioden — imorgon debiteras kortet.'
              : `${daysLeft} dag${daysLeft === 1 ? '' : 'ar'} kvar av provperioden — kortet debiteras automatiskt efteråt.`}
          </span>
          <button
            onClick={() => setShowCancelModal(true)}
            className="text-amber-900 dark:text-amber-200 font-semibold underline hover:no-underline"
            data-testid="trial-banner-cancel-btn"
          >
            Säg upp gratis →
          </button>
          <button
            onClick={() => setBannerDismissed(true)}
            className="text-amber-700 dark:text-amber-400 hover:text-amber-900 p-0.5"
            aria-label="Stäng"
            data-testid="trial-banner-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {children}

      {isPaused && <PausedOverlay onLogout={logout} />}

      {showCancelModal && (
        <CancelModal
          state={state}
          cancelling={cancelling}
          onCancel={handleCancel}
          onClose={() => setShowCancelModal(false)}
        />
      )}

      {/* Small info ribbon if cancellation is scheduled (post-trial cancel_at_period_end) */}
      {state?.cancel_at_period_end && !isCancelled && !isPaused && (
        <CancellationScheduledRibbon state={state} onUndo={fetchStatus} />
      )}
    </>
  );
};

const PausedOverlay = ({ onLogout }) => (
  <div
    className="fixed inset-0 z-[100] bg-[#1A1A1A]/95 backdrop-blur-md flex items-center justify-center px-4"
    data-testid="paused-overlay"
  >
    <div className="max-w-md w-full bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E8E2D9] dark:border-[#3A3530] p-8 text-center">
      <div className="w-16 h-16 mx-auto bg-[#E8613A]/10 rounded-full flex items-center justify-center mb-5">
        <Pause size={28} className="text-[#E8613A]" />
      </div>
      <img src="/operio-mascot-3d.png" alt="" className="w-20 h-20 mx-auto -mt-2 mb-3" />
      <h1
        className="text-2xl font-bold tracking-tight text-foreground"
        style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
      >
        Operio är pausad
      </h1>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Vi kunde inte debitera ditt kort. Uppdatera betalningsmetoden för att låsa upp kontot direkt.
      </p>
      <a
        href="/dashboard/settings?billing=1"
        className="block w-full mt-6 h-12 leading-[3rem] bg-[#E8613A] hover:bg-[#D4532F] text-white font-semibold rounded-full text-sm"
        data-testid="paused-update-card-btn"
      >
        Uppdatera kort
      </a>
      <button
        onClick={onLogout}
        className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="paused-logout-btn"
      >
        Logga ut
      </button>
    </div>
  </div>
);

const CancellationScheduledRibbon = ({ state, onUndo }) => {
  const [undoing, setUndoing] = useState(false);
  const periodEnd = state?.current_period_end ? new Date(state.current_period_end).toLocaleDateString('sv-SE') : '';

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await axios.post(`${API_URL}/subscription/reactivate`, {}, { withCredentials: true });
      toast.success('Uppsägning återtagen.');
      onUndo();
    } catch (err) {
      toast.error('Kunde inte återta uppsägningen');
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 left-4 bg-[#1A1A1A] text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 text-xs max-w-xs z-40"
      data-testid="cancellation-ribbon"
    >
      <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
      <span>Uppsagd — åtkomst t.o.m. <strong>{periodEnd}</strong></span>
      <button
        onClick={handleUndo}
        disabled={undoing}
        className="text-amber-400 hover:text-amber-300 font-semibold underline disabled:opacity-50"
        data-testid="cancellation-undo-btn"
      >
        {undoing ? '...' : 'Ångra'}
      </button>
    </div>
  );
};

const CancelModal = ({ state, cancelling, onCancel, onClose }) => {
  const isInTrial = state?.status === 'trial' || state?.status === 'trialing';
  const trialEnd = state?.trial_end ? new Date(state.trial_end).toLocaleDateString('sv-SE') : '';
  const periodEnd = state?.current_period_end ? new Date(state.current_period_end).toLocaleDateString('sv-SE') : '';

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="cancel-modal"
    >
      <div
        className="max-w-md w-full bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#1A1A1A] text-white px-6 py-5 flex items-center gap-3">
          <AlertTriangle size={18} className="text-amber-400" />
          <h2 className="text-base font-semibold">Säg upp prenumerationen?</h2>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-foreground">
          {isInTrial ? (
            <>
              <p className="leading-relaxed">
                Du befinner dig i din 14-dagars provperiod (slutar <strong>{trialEnd}</strong>).
                Säger du upp nu avslutas tjänsten <strong>direkt</strong> och inget kort debiteras.
              </p>
              <p className="text-xs text-muted-foreground">
                Du loggas ut efter uppsägning. Du är välkommen tillbaka när som helst.
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                Din prenumeration är aktiv. Säger du upp nu fortsätter du ha åtkomst fram till{' '}
                <strong>{periodEnd}</strong>. Därefter avslutas tjänsten och inga fler debiteringar görs.
              </p>
              <p className="text-xs text-muted-foreground">
                Bindningstid kan medföra att slutdatumet är längre fram — Stripe beräknar exakt.
              </p>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="cancel-modal-back-btn"
          >
            Avbryt
          </button>
          <Button
            onClick={onCancel}
            disabled={cancelling}
            variant="destructive"
            className="h-10 px-5 rounded-full"
            data-testid="cancel-modal-confirm-btn"
          >
            {cancelling ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Säger upp...</>
            ) : (
              isInTrial ? 'Ja, säg upp direkt' : 'Ja, säg upp vid periodens slut'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGuard;
